import { decodeOrientedImage } from './oriented-image';
import { webpDimensions } from './webp-dimensions';
import { createDocument, validateDimensions } from '../core/model';
import { RasterTileStore } from '../core/tiles';
import type { CodecContext, CodecReadResult, DocumentCodec } from './types';
import { throwIfAborted } from './types';

const supportedTypes = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/bmp']);
const supportedExtensions = /\.(png|jpe?g|webp|gif|bmp)$/i;
const MAX_SOURCE_BYTES = 512 * 1024 * 1024;

async function preflightDimensions(file: File): Promise<{ width: number; height: number } | null> {
  const bytes = new Uint8Array(await file.slice(0, Math.min(file.size, 1024 * 1024)).arrayBuffer());
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (bytes.length >= 24 && view.getUint32(0, false) === 0x89504e47 && view.getUint32(4, false) === 0x0d0a1a0a) {
    return { width: view.getUint32(16, false), height: view.getUint32(20, false) };
  }
  if (bytes.length >= 4 && bytes[0] === 0xff && bytes[1] === 0xd8) {
    let offset = 2;
    while (offset + 9 < bytes.length) {
      if (bytes[offset] !== 0xff) { offset += 1; continue; }
      const marker = bytes[offset + 1] ?? 0;
      if ([0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf].includes(marker)) {
        return { height: view.getUint16(offset + 5, false), width: view.getUint16(offset + 7, false) };
      }
      if (marker === 0xd8 || marker === 0xd9) { offset += 2; continue; }
      const length = view.getUint16(offset + 2, false);
      if (length < 2) break;
      offset += 2 + length;
    }
  }
  // GIF87a/GIF89a: logical screen size is little-endian at bytes 6..9.
  if (bytes.length >= 10 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
    return { width: view.getUint16(6, true), height: view.getUint16(8, true) };
  }
  // BMP: DIB header width/height (little-endian int32) at offsets 18 and 22.
  if (bytes.length >= 26 && bytes[0] === 0x42 && bytes[1] === 0x4d) {
    return { width: Math.abs(view.getInt32(18, true)), height: Math.abs(view.getInt32(22, true)) };
  }
  return webpDimensions(bytes, file.size);
}

export class BrowserImageCodec implements DocumentCodec {
  readonly id = 'browser-image';
  readonly label = 'Browser image decoder';

  canRead(file: File): boolean {
    return supportedTypes.has(file.type) || supportedExtensions.test(file.name);
  }

  async read(file: File, context: CodecContext = {}): Promise<CodecReadResult> {
    context.onProgress?.({ phase: 'validating', progress: 0.05, message: 'Checking image dimensions' });
    throwIfAborted(context.signal);
    if (file.size > MAX_SOURCE_BYTES) throw new Error('Image file exceeds the 512 MiB compressed-file safety limit.');
    const dimensions = await preflightDimensions(file);
    throwIfAborted(context.signal);
    if (dimensions) validateDimensions(dimensions.width, dimensions.height);
    const bitmap = await decodeOrientedImage(file, { imageOrientation: 'from-image', colorSpaceConversion: 'default', premultiplyAlpha: 'none' }, context.signal, true);
    try {
      validateDimensions(bitmap.width, bitmap.height);
      throwIfAborted(context.signal);
      context.onProgress?.({ phase: 'decoding', progress: 0.45, message: 'Decoding pixels' });
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const renderingContext = canvas.getContext('2d', { willReadFrequently: true });
      if (!renderingContext) throw new Error('Canvas 2D is unavailable.');
      renderingContext.drawImage(bitmap, 0, 0);
      const pixels = renderingContext.getImageData(0, 0, bitmap.width, bitmap.height);
      throwIfAborted(context.signal);
      const editorDocument = createDocument({
        name: file.name.replace(/\.[^.]+$/, ''),
        width: bitmap.width,
        height: bitmap.height,
        background: 'transparent',
      });
      const layer = editorDocument.root.children[0];
      if (!layer || layer.kind !== 'raster') throw new Error('New document did not contain a raster layer.');
      layer.name = file.name;
      layer.raster = RasterTileStore.fromImageData(pixels);
      editorDocument.source = { kind: 'image', name: file.name };
      context.onProgress?.({ phase: 'converting', progress: 1, message: 'Image ready' });
      return { document: editorDocument };
    } finally {
      bitmap.close();
    }
  }
}
